import { TourDefinition, TourStep } from "./types";
import { getInvoicesTagLevels } from "../../../docuclipper/api";

// All sandbox tours live inside the tree workspace at /extractdata.
// Different sub-views are selected via query string (docType + mode + analyzeMode).

const BASE_QS = "l1=Default+Folder&l2=Default+Project";
const ROUTE_BANK = `/extractdata?${BASE_QS}&docType=bank&mode=reconcile&analyzeMode=reconcile`;
const ROUTE_CATEGORIZE = `/extractdata?${BASE_QS}&docType=bank&mode=categorize&analyzeMode=categorize`;
const ROUTE_ANALYZE = `/extractdata?${BASE_QS}&docType=bank&mode=analyze&analyzeMode=flow`;
const ROUTE_ANALYZE_GAPS = `/extractdata?${BASE_QS}&docType=bank&mode=analyze&analyzeMode=gaps`;
const ROUTE_ANALYZE_TRANSFERS = `/extractdata?${BASE_QS}&docType=bank&mode=analyze&analyzeMode=match`;
const ROUTE_ANALYZE_RECURRING = `/extractdata?${BASE_QS}&docType=bank&mode=analyze&analyzeMode=recurring`;
const ROUTE_ANALYZE_CASHFLOW = `/extractdata?${BASE_QS}&docType=bank&mode=analyze&analyzeMode=cashflow`;
const ROUTE_ANALYZE_TAGS = `/extractdata?${BASE_QS}&docType=bank&mode=analyze&analyzeMode=transactionTags`;
const SEL_ANALYZE_TAB_GAPS = "#admin-tabs-tab-2";
const SEL_ANALYZE_TAB_TRANSFERS = "#admin-tabs-tab-3";
const SEL_ANALYZE_TAB_RECURRING = "#admin-tabs-tab-9";
const SEL_ANALYZE_TAB_CASHFLOW = "#admin-tabs-tab-7";
const SEL_ANALYZE_TAB_TAGS = "#admin-tabs-tab-8";
const ROUTE_FRAUD = `/extractdata?${BASE_QS}&docType=bank&mode=fraud&analyzeMode=fraud`;
const ROUTE_DOCUMENTS = `/extractdata?${BASE_QS}&docType=bank&mode=documents&analyzeMode=reconcile`;
const ROUTE_INVOICES = `/extractdata?${BASE_QS}&docType=invoices&mode=invoices`;
const ROUTE_INVOICES_APQ = `/extractdata?${BASE_QS}&docType=invoices&mode=inbox`;
const ROUTE_INVOICES_ANALYTICS = `/extractdata?${BASE_QS}&docType=invoices&mode=analytics`;
const ROUTE_FORMS = `/extractdata?${BASE_QS}&docType=forms&mode=view`;
const ROUTE_OTHER = `/extractdata?${BASE_QS}&docType=other&mode=templates`;

/**
 * Build the invoice-detail URL for whichever invoice happens to be first in
 * the current project. We avoid hard-coding an invoice ID because real user
 * accounts (and re-seeded sandboxes) will have different IDs.
 */
async function resolveFirstInvoiceDetailRoute(): Promise<string | null> {
  // No caching on purpose: TourContext already short-circuits resolveRoute
  // when consecutive steps share the same fallback route, so this only runs
  // once per entry into the invoice-detail segment of the tour. A cached ID
  // would go stale after a reseed or when the first invoice changes.
  try {
    const rsp: any = await getInvoicesTagLevels("Default Folder", "Default Project", {
      limit: 1,
      offset: 0,
    } as any);
    const rows = rsp?.transactions || rsp?.rows || rsp?.data || [];
    const first = Array.isArray(rows) ? rows[0] : null;
    // The URL's `invoiceId` param is actually the TransactionLine numeric id
    // (the backend parses it as bigint). `first.invoiceId` is the human-readable
    // invoice *number* string (e.g. "INV-2026-0418") — using that crashes with
    // "invalid input syntax for type bigint". Prefer `id` / `transactionLineId`.
    const id = first?.id || first?.transactionLineId;
    if (!id || Number.isNaN(Number(id))) return null;
    return `/extractdata?${BASE_QS}&docType=invoices&mode=inbox&invoiceId=${encodeURIComponent(
      String(id)
    )}`;
  } catch {
    return null;
  }
}

const SEL_TREE = ".treeview-tour-add-entity";
const SEL_DOC_TABS = "#project-doc-type-tabs-tab-bank";
const SEL_TAB_INVOICES = "#project-doc-type-tabs-tab-invoices";
const SEL_TAB_FORMS = "#project-doc-type-tabs-tab-forms";
const SEL_SUB_TRANSACTIONS = '[data-tour-key="reconcile"]';
const SEL_SUB_CATEGORIZE = '[data-tour-key="categorize"]';
const SEL_SUB_ANALYZE = '[data-tour-key="analyze"]';
const SEL_SUB_FRAUD = '[data-tour-key="fraud"]';
const SEL_SUB_DOCUMENTS = '[data-tour-key="documents"]';
const SEL_UPLOAD = ".btn--project-add-documents";
const SEL_DOCUMENT_PICKER = ".transation-manager-select-document-id, .new-account-summary";
// Anchor to the Transactions grid specifically. The Categorize view also has
// a Category Breakdown grid at the top, so a bare `.ag-root-wrapper` selector
// (first-match wins) highlights the wrong one. Prefer the new data-tour-key;
// fall back to the older tour-tour-specific class and finally `.ag-root-wrapper`.
const SEL_TRANSACTIONS =
  '[data-tour-key="categorize-transactions"], .categorization-tour-table, .new-transactions, .ag-root-wrapper';
const SEL_DOWNLOAD = ".btn--download-data";

// Common opening and closing steps reused across all segment tours.
function welcomeStep(_body: string): TourStep {
  return {
    route: ROUTE_BANK,
    selector: null,
    title: "Welcome to DocuClipper",
    body:
      "DocuClipper converts PDFs into structured data: bank and credit-card statements, invoices and receipts, W-2s, paystubs, 1099s, checks, and custom templates. Integrations push the results to QuickBooks, Xero, Excel, Google Sheets, and more. This sandbox is loaded with sample data — click through to explore, then sign up to process your own files.",
    placement: "bottom"
  };
}

function workspaceStep(): TourStep {
  return {
    route: ROUTE_BANK,
    selector: SEL_TREE,
    title: "Your workspace",
    body: "Folders and projects keep documents organized. This sandbox has one folder and one project pre-loaded with sample data.",
    placement: "right"
  };
}

function uploadStep(body: string): TourStep {
  return {
    route: ROUTE_BANK,
    selector: SEL_UPLOAD,
    title: "Add your own document",
    body,
    placement: "left"
  };
}

function downloadStep(body: string): TourStep {
  return {
    route: ROUTE_BANK,
    selector: SEL_DOWNLOAD,
    title: "Download the data",
    body,
    placement: "left"
  };
}

function closingStep(body: string): TourStep {
  return {
    route: ROUTE_BANK,
    selector: null,
    title: "Ready to try it for real?",
    body,
    placement: "bottom"
  };
}

// ---------------------------------------------------------------------------
// Segment-specific tours
// ---------------------------------------------------------------------------

export const DEMO_TOUR_ACCOUNTANT: TourDefinition = {
  id: "demo-accountant",
  steps: [
    welcomeStep("This demo shows a client's bank statement converted to structured data — the kind you book into QuickBooks or Xero."),
    workspaceStep(),
    {
      route: ROUTE_BANK,
      selector: SEL_DOCUMENT_PICKER,
      title: "The client's statement",
      body: "Totals, credits, and debits are already computed. No manual entry.",
      placement: "bottom"
    },
    {
      route: ROUTE_BANK,
      selector: SEL_TRANSACTIONS,
      title: "Every transaction, structured",
      body: "Date, description, amount, balance — all editable before you book it.",
      placement: "top"
    },
    {
      route: ROUTE_CATEGORIZE,
      selector: SEL_SUB_CATEGORIZE,
      title: "Auto-categorize",
      body: "DocuClipper suggests categories for each transaction so you're not coding every line by hand.",
      placement: "bottom"
    },
    {
      route: ROUTE_INVOICES,
      selector: SEL_TAB_INVOICES,
      title: "Invoices and receipts too",
      body: "Upload vendor bills and receipts here — DocuClipper extracts vendor, date, and line items for you.",
      placement: "bottom"
    },
    uploadStep("In a real account, drop any client PDF here. Every major bank format is supported."),
    downloadStep("Export to Excel, CSV, or push straight to QuickBooks or Xero."),
    closingStep("Sign up to convert your own client documents — most accountants save hours per engagement.")
  ]
};

export const DEMO_TOUR_BOOKKEEPER: TourDefinition = {
  id: "demo-bookkeeper",
  steps: [
    welcomeStep("This demo shows a restaurant bank account — high-volume daily transactions, payroll, and vendor payments."),
    workspaceStep(),
    {
      route: ROUTE_BANK,
      selector: SEL_DOCUMENT_PICKER,
      title: "Daily batches already extracted",
      body: "POS batches, payroll runs, and vendor payments are parsed from the statement.",
      placement: "bottom"
    },
    {
      route: ROUTE_BANK,
      selector: SEL_TRANSACTIONS,
      title: "Clean, structured rows",
      body: "Every line is editable before it lands in the books.",
      placement: "top"
    },
    {
      route: ROUTE_CATEGORIZE,
      selector: SEL_SUB_CATEGORIZE,
      title: "Bulk categorize",
      body: "Assign categories in bulk with rules you control — not one-by-one.",
      placement: "bottom"
    },
    uploadStep("In a real account, drop your client's PDF here. Works with any bank, credit union, or business account."),
    downloadStep("Send directly to QuickBooks, Xero, or CSV for any accounting tool."),
    closingStep("Most bookkeepers save 20–30 minutes per client per month. Sign up free to try with your own data.")
  ]
};

export const DEMO_TOUR_BUSINESS_OWNER: TourDefinition = {
  id: "demo-business-owner",
  steps: [
    welcomeStep("This demo shows 3 months of a business bank account — the kind you'd review for cash flow."),
    workspaceStep(),
    {
      route: ROUTE_BANK,
      selector: SEL_DOCUMENT_PICKER,
      title: "The sample statement",
      body: "Totals, credits, debits, and transaction count — already computed.",
      placement: "bottom"
    },
    {
      route: ROUTE_BANK,
      selector: SEL_TRANSACTIONS,
      title: "Every line extracted",
      body: "Date, description, amount, running balance — structured and editable.",
      placement: "top"
    },
    {
      route: ROUTE_ANALYZE,
      selector: SEL_SUB_ANALYZE,
      title: "See your cash flow",
      body: "Advanced Analysis charts inflow vs outflow across the period — you spot trends without building a spreadsheet.",
      placement: "bottom"
    },
    uploadStep("Drop any bank's PDF here — Chase, Wells Fargo, BofA, credit unions, all work."),
    downloadStep("Export as Excel or CSV for your bookkeeper, or push directly into QuickBooks or Xero."),
    closingStep("Sign up free and upload your first bank statement — no credit card required.")
  ]
};

export const DEMO_TOUR_LENDER: TourDefinition = {
  id: "demo-lender",
  steps: [
    welcomeStep("This demo shows an applicant's bank statement — the kind you review for income verification."),
    workspaceStep(),
    {
      route: ROUTE_BANK,
      selector: SEL_DOCUMENT_PICKER,
      title: "Parsed statement",
      body: "Totals, credits, and debits summarized on the right — no manual adding.",
      placement: "bottom"
    },
    {
      route: ROUTE_BANK,
      selector: SEL_TRANSACTIONS,
      title: "Every deposit and withdrawal",
      body: "Structured rows you can scan for recurring income and flag unusual activity.",
      placement: "top"
    },
    {
      route: ROUTE_ANALYZE,
      selector: SEL_SUB_ANALYZE,
      title: "Income pattern at a glance",
      body: "Advanced Analysis surfaces recurring deposits and cash-flow patterns — the same signals you build spreadsheets for.",
      placement: "bottom"
    },
    {
      route: ROUTE_FORMS,
      selector: SEL_TAB_FORMS,
      title: "Pay stubs and W-2s",
      body: "Forms holds paystubs, W-2s, and 1099s — extracted into fields you can drop into your worksheet.",
      placement: "bottom"
    },
    uploadStep("In a real account, drop the applicant's statements here. Multi-page, multi-month — no configuration."),
    downloadStep("Export to Excel for your income-calculation worksheet, or push into your LOS."),
    closingStep("Sign up to process your own loan packages — underwriters go from hours to minutes per file.")
  ]
};

export const DEMO_TOUR_FINANCE_TEAM: TourDefinition = {
  id: "demo-finance-team",
  steps: [
    welcomeStep("This demo shows an AP workflow: vendor invoices alongside bank payments, ready to match."),
    workspaceStep(),
    {
      route: ROUTE_BANK,
      selector: SEL_DOCUMENT_PICKER,
      title: "Bank side: payments",
      body: "ACH payments and wires extracted from the statement, ready to reconcile.",
      placement: "bottom"
    },
    {
      route: ROUTE_INVOICES,
      selector: SEL_TAB_INVOICES,
      title: "Invoice side: bills",
      body: "Vendor, invoice number, date, and line items extracted from each PDF.",
      placement: "bottom"
    },
    {
      route: ROUTE_CATEGORIZE,
      selector: SEL_SUB_CATEGORIZE,
      title: "Match and categorize",
      body: "Apply rules across batches so recurring vendors don't need manual coding each month.",
      placement: "bottom"
    },
    uploadStep("Drop a batch of invoices or statements here — DocuClipper processes them in parallel."),
    downloadStep("Export the matched data into your ERP or accounting system for final reconciliation."),
    closingStep("Sign up to automate your AP matching — most teams save 3–5 hours per week.")
  ]
};

export const DEMO_TOUR_INVESTIGATOR: TourDefinition = {
  id: "demo-investigator",
  steps: [
    welcomeStep("This demo shows a subject's account with suspicious patterns — the kind you'd analyze in a financial investigation."),
    workspaceStep(),
    {
      route: ROUTE_BANK,
      selector: SEL_DOCUMENT_PICKER,
      title: "Every entry extracted",
      body: "Wires, cash withdrawals, transfers — all captured and ready to sort.",
      placement: "bottom"
    },
    {
      route: ROUTE_BANK,
      selector: SEL_TRANSACTIONS,
      title: "Transactions you can filter",
      body: "Sort by amount, date, or counterparty — no copy-paste from PDFs.",
      placement: "top"
    },
    {
      route: ROUTE_FRAUD,
      selector: SEL_SUB_FRAUD,
      title: "Fraud detection",
      body: "Automatic flags for structured deposits, rapid transfers, and irregular timing — starts you on the right leads.",
      placement: "bottom"
    },
    {
      route: ROUTE_ANALYZE,
      selector: SEL_SUB_ANALYZE,
      title: "Advanced analysis",
      body: "Cash-flow charts and counterparty rollups help you build the timeline without pivot tables.",
      placement: "bottom"
    },
    uploadStep("In a real account, bulk-upload dozens of statements across years — DocuClipper keeps them per project."),
    downloadStep("Export to Excel for pivot analysis, or push into your case management system."),
    closingStep("Sign up to process your own document sets — no limit on statements per case.")
  ]
};

export const DEMO_TOUR_OTHER: TourDefinition = {
  id: "demo-other",
  steps: [
    welcomeStep("DocuClipper converts bank statements, invoices, and financial documents into structured data you can use."),
    workspaceStep(),
    {
      route: ROUTE_BANK,
      selector: SEL_DOC_TABS,
      title: "Four document types",
      body: "Bank/CC Transactions, Invoices/Receipts, Forms, and Other Documents — all in one workspace.",
      placement: "bottom"
    },
    {
      route: ROUTE_BANK,
      selector: SEL_DOCUMENT_PICKER,
      title: "Sample data",
      body: "Totals, credits, and debits already computed for the loaded statement.",
      placement: "bottom"
    },
    {
      route: ROUTE_BANK,
      selector: SEL_TRANSACTIONS,
      title: "Extracted rows",
      body: "Filter, sort, and edit before exporting.",
      placement: "top"
    },
    uploadStep("In a real account, drag and drop any PDF here — no templates to configure."),
    downloadStep("Export to Excel, CSV, or connect to QuickBooks, Xero, Google Sheets, and more."),
    closingStep("Sign up free to convert your own documents — no credit card required.")
  ]
};

// ---------------------------------------------------------------------------
// Sub-tours (L2) — deeper walkthroughs, offered after the segment overview
// ---------------------------------------------------------------------------

export const SUB_TOUR_BANK: TourDefinition = {
  id: "sub-bank",
  steps: [
    {
      route: ROUTE_BANK,
      selector: SEL_DOCUMENT_PICKER,
      title: "Bank statements, deeper",
      body: "Each statement becomes a row on the right with totals, credits, and debits pre-computed. Swap statements from this picker.",
      placement: "bottom"
    },
    {
      route: ROUTE_BANK,
      selector: SEL_TRANSACTIONS,
      title: "Reconcile and edit",
      body: "Click any cell to edit. Running balance is verified against the bank totals — mismatches are flagged.",
      placement: "top"
    },
    {
      route: ROUTE_CATEGORIZE,
      selector: SEL_SUB_CATEGORIZE,
      title: "Rules-based categorization",
      body: "Write a rule once; it applies to every matching transaction across every statement in this project.",
      placement: "bottom"
    },
    {
      route: ROUTE_DOCUMENTS,
      selector: SEL_SUB_DOCUMENTS,
      title: "Manage the source PDFs",
      body: "Documents holds the original PDFs — re-process, password-protect, or delete from here.",
      placement: "bottom"
    },
    {
      route: ROUTE_BANK,
      selector: SEL_DOWNLOAD,
      title: "Export to QuickBooks, Xero, or Excel",
      body: "One click sends the reconciled data to your accounting tool, or exports to CSV/Excel for anywhere else.",
      placement: "left"
    }
  ]
};

// The invoice detail view is reached via `&invoiceId=<id>` on the inbox route.
// Because IDs are per-tenant, we resolve the first invoice at tour-time via API
// and fall back to the plain inbox list if none can be found.
const INVOICE_DETAIL_FALLBACK_ROUTE = ROUTE_INVOICES_APQ;

export const SUB_TOUR_INVOICES: TourDefinition = {
  id: "sub-invoices",
  steps: [
    {
      route: ROUTE_INVOICES,
      selector: SEL_TAB_INVOICES,
      title: "Invoice workspace",
      body: "Invoices and receipts are processed as their own doc type — vendor, customer, date, line items, and tax are all extracted automatically.",
      placement: "bottom"
    },
    {
      route: ROUTE_INVOICES,
      selector: ".ag-root-wrapper, .invoice-tour-list",
      title: "Invoices list",
      body: "Every invoice in the project shows up here. Click any row to open the full invoice editor — PDF on one side, extracted fields on the other.",
      placement: "top"
    },
    // Opens a specific invoice. resolveRoute picks the first real invoice in
    // the project so the step works in dev, sandbox, and production alike.
    {
      route: INVOICE_DETAIL_FALLBACK_ROUTE,
      resolveRoute: resolveFirstInvoiceDetailRoute,
      selector: ".invoice-tour-fields",
      title: "Editable invoice fields",
      body: "Every extracted field is editable — vendor, invoice number, dates, totals, tax. Approvals and exports feed off these values.",
      placement: "left"
    },
    {
      route: INVOICE_DETAIL_FALLBACK_ROUTE,
      resolveRoute: resolveFirstInvoiceDetailRoute,
      selector: '[data-cy="invoice-section-approvals"]',
      title: "Approvals workflow",
      body: "Route invoices through approvers before they sync. Submit for approval, approve, or reject — the audit trail is captured per invoice.",
      placement: "left"
    },
    {
      route: INVOICE_DETAIL_FALLBACK_ROUTE,
      resolveRoute: resolveFirstInvoiceDetailRoute,
      selector: '[data-cy="invoice-section-automation"]',
      title: "Per-vendor rules",
      body: "Define rules once per vendor — categories, GL codes, customer refs — and the next invoice from that vendor auto-fills them.",
      placement: "left"
    },
    {
      route: INVOICE_DETAIL_FALLBACK_ROUTE,
      resolveRoute: resolveFirstInvoiceDetailRoute,
      selector: '[data-cy="invoice-section-po-matching"]',
      title: "PO matching",
      body: "Link an invoice to its purchase order so quantities and amounts are checked automatically. Mismatches surface as exceptions.",
      placement: "left"
    },
    {
      route: ROUTE_INVOICES_APQ,
      selector: '[data-tour-key="invoice-view-apq"], .ag-root-wrapper',
      title: "AP Queue",
      body: "The AP Queue shows invoices that need review, exceptions, and approved invoices ready to sync — the daily dashboard for AP teams.",
      placement: "bottom"
    },
    {
      route: ROUTE_INVOICES_ANALYTICS,
      selector: '[data-tour-key="invoice-view-analytics"], .ag-root-wrapper',
      title: "Analytics",
      body: "Total spend, invoice volume, top vendors, duplicates, and trends over time — payables visibility without a pivot table.",
      placement: "bottom"
    }
  ]
};

export const SUB_TOUR_CATEGORIZE: TourDefinition = {
  id: "sub-categorize",
  steps: [
    {
      route: ROUTE_CATEGORIZE,
      selector: SEL_SUB_CATEGORIZE,
      title: "Categorization",
      body: "Every transaction gets an auto-assigned category based on its description. You stay in control — override any row and DocuClipper remembers.",
      placement: "bottom"
    },
    {
      route: ROUTE_CATEGORIZE,
      selector: SEL_TRANSACTIONS,
      title: "Change a category manually",
      body: "Click the category cell on any row to pick a different one from the dropdown. Your change overrides the auto-assignment for that transaction.",
      placement: "top"
    },
    {
      route: ROUTE_CATEGORIZE,
      selector: '[data-tour-key="categorize-rules"]',
      title: "Customize categories and keywords",
      body: "Add your own categories and define the keywords that match them. Rules are plain keyword matches (regex supported) — no complicated setup.",
      placement: "right"
    },
    {
      route: ROUTE_CATEGORIZE,
      selector: '[data-tour-key="categorize-uncategorized"], [data-tour-key="categorize-charts"]',
      title: "Spot uncategorized items",
      body: "Anything that didn't match a rule is flagged as Uncategorized with a badge. Work through those one-by-one, or add a rule to clear them in bulk.",
      placement: "bottom"
    },
    {
      route: ROUTE_CATEGORIZE,
      selector: '[data-tour-key="categorize-charts"], .categorization-tour-summary',
      title: "Charts refresh instantly",
      body: "Pie and bar charts on the right update the moment you edit a category — no save button. Export the categorized data to Excel, QuickBooks, or Xero when ready.",
      placement: "top"
    }
  ]
};

export const SUB_TOUR_FRAUD: TourDefinition = {
  id: "sub-fraud",
  steps: [
    {
      route: ROUTE_FRAUD,
      selector: SEL_SUB_FRAUD,
      title: "Fraud detection",
      body: "DocuClipper flags anomalies like structured deposits, rapid transfers, and font inconsistencies in the source PDF.",
      placement: "bottom"
    },
    {
      route: ROUTE_FRAUD,
      selector: null,
      title: "Score + signals",
      body: "Each document gets a fraud score plus individual signals you can expand for evidence.",
      placement: "bottom"
    },
    {
      route: ROUTE_FRAUD,
      selector: null,
      title: "Useful for lenders and investigators",
      body: "Scores persist with the document so you can triage a stack of applications or subjects quickly.",
      placement: "bottom"
    }
  ]
};

// ---------------------------------------------------------------------------
// Per-segment Financial Analysis tour
// ---------------------------------------------------------------------------
// Same 7-step structure for every segment; only the body copy changes so each
// persona hears the feature described in their own language. `buildAnalyzeTour`
// is the single source of truth — adding a segment is one row in ANALYZE_COPY.
// ---------------------------------------------------------------------------

type AnalyzeSegment =
  | "accountant"
  | "bookkeeper"
  | "business_owner"
  | "lender"
  | "finance_team"
  | "financial_investigator"
  | "other";

interface AnalyzeCopy {
  intro: string;
  cashflow: string;
  gaps: string;
  transfers: string;
  recurring: string;
  tagging: string;
  exportOut: string;
}

const ANALYZE_COPY: Record<AnalyzeSegment, AnalyzeCopy> = {
  accountant: {
    intro: "Five analysis views built for client engagements — cash flow, date gaps, transfers, recurring patterns, and tagging. Every view rolls up across every statement in the project so your workpapers write themselves.",
    cashflow: "Month-by-month inflow vs. outflow for the client, ready to drop into a review package or trial-balance binder. Spot unusual swings before the client's controller does.",
    gaps: "Coverage + reconciliation grid: every month × every account, color-coded by whether the statement is present and reconciled. Spot missing periods and unreconciled balances in one glance before you finalize the engagement.",
    transfers: "Matches paired debits/credits across the client's operating, payroll, and savings accounts so internal movements don't double-count as revenue or expense on the P&L.",
    recurring: "Surfaces recurring vendor payments, payroll runs, loan servicing — the stuff you'd otherwise hunt through 90 pages of statements to verify against the GL.",
    tagging: "Tag by client, engagement, or workpaper reference. When PBC season rolls around you filter to just what you need without re-cutting the data.",
    exportOut: "Export any view to Excel and drop it straight into your workpapers, or push the underlying rows into QuickBooks / Xero with the category assignments preserved."
  },
  bookkeeper: {
    intro: "Five views that cut the monthly close in half: cash flow, date gaps, transfers, recurring, and tagging. All automatic, all cross-statement — no pivot tables.",
    cashflow: "Monthly inflow vs. outflow for every client you manage — a one-screen review you can share before monthly close calls instead of rebuilding it each time.",
    gaps: "One grid showing every month × every account with reconciliation status baked in. See what's missing, what's imported-but-unreconciled, and what's clean — without opening a single PDF.",
    transfers: "Finds inter-account transfers (operating ↔ payroll, operating ↔ savings) automatically so you don't hand-code them or accidentally book them as P&L hits.",
    recurring: "Recurring rent, payroll, SaaS subscriptions, loan payments — all detected and grouped so repeat categorization takes one rule per vendor.",
    tagging: "Tag by client, location, or cost center. Great for splitting shared expenses across entities at month-end.",
    exportOut: "Export to Excel for client review packets, or push the categorized transactions directly into QuickBooks Online, Xero, or your spreadsheet of choice."
  },
  business_owner: {
    intro: "Five views that tell you where your money actually goes — cash flow, date gaps, transfers, recurring, and tagging. No accountant required to read them.",
    cashflow: "Monthly cash in vs. cash out across every account you upload. See the runway story at a glance — no building a spreadsheet from scratch.",
    gaps: "A heatmap of your accounts by month — red means a missing statement, amber means imported but not reconciled, green means clean. Don't make decisions off a partial picture.",
    transfers: "Automatically matches transfers between your business accounts so you don't see them as income or expenses — your real operating numbers, not accounting noise.",
    recurring: "Every subscription, loan payment, payroll run, and recurring vendor bill, surfaced automatically. The fastest way to spot a forgotten subscription you're still paying for.",
    tagging: "Tag transactions by project, property, product line — whatever you track — and filter the whole analysis to just that slice of the business.",
    exportOut: "Export to Excel for your bookkeeper, or connect QuickBooks / Xero so the categorized data flows straight in — no re-entry."
  },
  lender: {
    intro: "Five underwriting-grade views: cash flow, date gaps, transfers, recurring flows, and tagging. Every view rolls up across months and accounts so you underwrite off one screen, not 12 PDFs.",
    cashflow: "Applicant's monthly inflow vs. outflow for the review period — the headline number for DTI and ability-to-repay. Exports directly into your income worksheet.",
    gaps: "File completeness at a glance: every account × every month, with reconciliation status per statement. Catch missing or unreconciled periods before underwriting — not after the committee meeting.",
    transfers: "Auto-detects transfers between the applicant's accounts so a single dollar doesn't count as income on both sides of a transfer — a common source of qualification errors.",
    recurring: "Recurring deposits (payroll, rental income, SSI) vs. recurring obligations (loan servicing, rent, utilities). The core of every qualification decision, extracted automatically.",
    tagging: "Tag transactions as personal / business / excluded, or by loan file. Re-runs the analysis against your taxonomy — essential for commercial or self-employed applicants.",
    exportOut: "Export every view to Excel for the credit file, or push into your LOS. The raw transactions export alongside, so your audit trail is intact."
  },
  finance_team: {
    intro: "Five views for the AP/AR team: cash flow, date gaps, transfers, recurring flows, and tagging. Cross-account, cross-month, one screen — no pivoting ERP exports.",
    cashflow: "Monthly cash in vs. cash out across every operating account — the picture the CFO asks for at the start of every close. Ready before you even open Excel.",
    gaps: "Cross-account coverage + reconciliation grid. If a bank feed dropped or a statement didn't land — or it landed but didn't reconcile — you see it here before close, not mid-audit.",
    transfers: "Inter-account transfers auto-matched so intercompany and treasury moves don't pollute the P&L. Critical for multi-entity finance teams.",
    recurring: "Recurring vendor spend, payroll, and AR deposits surfaced with amounts and cadence — the feed for vendor-level spend reviews and forecasts.",
    tagging: "Tag by entity, department, cost center, or project. Filter the whole workspace by tag for spot audits or budget-vs-actual reviews.",
    exportOut: "Export to Excel for leadership reviews, or push the categorized transactions into NetSuite, Sage Intacct, QuickBooks, or Xero with tag metadata intact."
  },
  financial_investigator: {
    intro: "Five views built for financial investigations: cash flow, date gaps, transfers, recurring flows, and tagging. Reconstruct the subject's financial picture across years and accounts without ever leaving the workspace.",
    cashflow: "Monthly inflow vs. outflow across every account on the subject — the summary chart that anchors the timeline exhibit.",
    gaps: "Every account × every month, colored by whether you have the statement and whether it reconciles to its stated balances. Missing periods and balance deltas surface immediately — the two most common forensic tells.",
    transfers: "Matches transfers between the subject's accounts — layering and structuring patterns jump out visually instead of living buried in spreadsheets.",
    recurring: "Recurring deposits and outflows become obvious: payroll-like patterns, rent, loan servicing, subscription-based laundering indicators.",
    tagging: "Tag transactions as relevant to specific counterparties, case theories, or timeline entries. The tag filter drives every view so exhibits stay consistent.",
    exportOut: "Export every view to Excel for pivot analysis or as case exhibits. The raw transactions export alongside, preserving chain of custody."
  },
  other: {
    intro: "Five ways to turn raw transactions into insight: cash flow, date gaps, transfers, recurring patterns, and tagging. Every view rolls up across every statement you upload.",
    cashflow: "Monthly cash in vs. cash out, across every account — the fastest way to understand the shape of the data.",
    gaps: "Coverage + reconciliation grid across every account and month — see what's missing, what's imported, and what's reconciled in one view.",
    transfers: "Auto-matches paired debits and credits across accounts so internal transfers don't double-count.",
    recurring: "Surfaces recurring deposits and recurring outflows with amounts and cadence.",
    tagging: "Tag transactions with your own labels and filter the analysis by tag.",
    exportOut: "Every view exports to Excel alongside the raw transactions."
  }
};

/**
 * Produce a Financial Analysis sub-tour whose copy is tailored to the given
 * segment. Structure and selectors are identical across segments — only the
 * body text changes.
 */
export function buildAnalyzeTour(segment: AnalyzeSegment): TourDefinition {
  const copy = ANALYZE_COPY[segment] || ANALYZE_COPY.other;
  return {
    id: `sub-analyze-${segment}`,
    steps: [
      {
        route: ROUTE_ANALYZE_CASHFLOW,
        selector: SEL_SUB_ANALYZE,
        title: "Financial analysis workspace",
        body: copy.intro,
        placement: "bottom"
      },
      {
        route: ROUTE_ANALYZE_CASHFLOW,
        selector: SEL_ANALYZE_TAB_CASHFLOW,
        title: "Cash flow analysis",
        body: copy.cashflow,
        placement: "bottom"
      },
      {
        route: ROUTE_ANALYZE_GAPS,
        selector: SEL_ANALYZE_TAB_GAPS,
        title: "Date gaps",
        body: copy.gaps,
        placement: "bottom"
      },
      {
        route: ROUTE_ANALYZE_TRANSFERS,
        selector: SEL_ANALYZE_TAB_TRANSFERS,
        title: "Transfers between accounts",
        body: copy.transfers,
        placement: "bottom"
      },
      {
        route: ROUTE_ANALYZE_RECURRING,
        selector: SEL_ANALYZE_TAB_RECURRING,
        title: "Recurring flow of funds",
        body: copy.recurring,
        placement: "bottom"
      },
      {
        route: ROUTE_ANALYZE_TAGS,
        selector: SEL_ANALYZE_TAB_TAGS,
        title: "Transaction tagging",
        body: copy.tagging,
        placement: "bottom"
      },
      {
        route: ROUTE_ANALYZE_CASHFLOW,
        selector: null,
        title: "Export the analysis",
        body: copy.exportOut,
        placement: "bottom"
      }
    ]
  };
}

// Generic id kept for backwards-compatibility with the SUB_TOURS_MENU entry
// and anywhere else code references "sub-analyze". Resolves to the "other"
// copy by default; segment-specific tours are built on demand below.
export const SUB_TOUR_ANALYZE: TourDefinition = {
  ...buildAnalyzeTour("other"),
  id: "sub-analyze"
};

export const SEGMENT_ANALYZE_TOURS: Record<AnalyzeSegment, TourDefinition> = {
  accountant: buildAnalyzeTour("accountant"),
  bookkeeper: buildAnalyzeTour("bookkeeper"),
  business_owner: buildAnalyzeTour("business_owner"),
  lender: buildAnalyzeTour("lender"),
  finance_team: buildAnalyzeTour("finance_team"),
  financial_investigator: buildAnalyzeTour("financial_investigator"),
  other: buildAnalyzeTour("other")
};

export const SUB_TOURS_MENU: { id: string; label: string; description: string; emoji: string }[] = [
  { id: SUB_TOUR_BANK.id, label: "Bank statements", description: "Reconcile, categorize, export", emoji: "🏦" },
  { id: SUB_TOUR_INVOICES.id, label: "Invoices", description: "Approvals, rules, QBO/Xero", emoji: "📄" },
  { id: SUB_TOUR_CATEGORIZE.id, label: "Categorization", description: "Rules and auto-coding", emoji: "🏷️" },
  { id: SUB_TOUR_FRAUD.id, label: "Fraud detection", description: "Signals and scores", emoji: "🚨" },
  { id: SUB_TOUR_ANALYZE.id, label: "Advanced analysis", description: "Cash flow and charts", emoji: "📊" }
];

export const SUB_TOURS: Record<string, TourDefinition> = {
  [SUB_TOUR_BANK.id]: SUB_TOUR_BANK,
  [SUB_TOUR_INVOICES.id]: SUB_TOUR_INVOICES,
  [SUB_TOUR_CATEGORIZE.id]: SUB_TOUR_CATEGORIZE,
  [SUB_TOUR_FRAUD.id]: SUB_TOUR_FRAUD,
  [SUB_TOUR_ANALYZE.id]: SUB_TOUR_ANALYZE,
  // Register every per-segment analyze tour so startTour(id) works for them.
  ...Object.fromEntries(
    Object.values(SEGMENT_ANALYZE_TOURS).map(t => [t.id, t])
  )
};

/**
 * Pick the analyze sub-tour id that best matches the given segment.
 * Falls back to the generic tour if the segment string is unknown.
 */
export function analyzeTourIdForSegment(segment?: string | null): string {
  if (!segment) return SUB_TOUR_ANALYZE.id;
  const keyed = (SEGMENT_ANALYZE_TOURS as Record<string, TourDefinition>)[segment];
  return keyed ? keyed.id : SUB_TOUR_ANALYZE.id;
}

// ---------------------------------------------------------------------------
// Onboarding tour — first-time real users, focused on next step
// ---------------------------------------------------------------------------

export const ONBOARDING_TOUR: TourDefinition = {
  id: "onboarding",
  steps: [
    {
      route: ROUTE_BANK,
      selector: SEL_UPLOAD,
      title: "Start here: upload a document",
      body: "Drop a PDF bank statement or invoice here. Your first conversion takes about 30 seconds.",
      placement: "bottom"
    },
    {
      route: ROUTE_BANK,
      selector: SEL_TRANSACTIONS,
      title: "Your extracted data",
      body: "Once processing finishes, transactions appear here — structured, editable, and ready to export.",
      placement: "top"
    },
    {
      route: ROUTE_BANK,
      selector: SEL_DOWNLOAD,
      title: "Download or connect an integration",
      body: "Export as Excel or CSV — or connect to QuickBooks, Xero, or Google Sheets from the integrations menu.",
      placement: "left"
    }
  ]
};

// ---------------------------------------------------------------------------
// Map segment → demo tour
// ---------------------------------------------------------------------------

export const SEGMENT_DEMO_TOURS: Record<string, TourDefinition> = {
  accountant: DEMO_TOUR_ACCOUNTANT,
  bookkeeper: DEMO_TOUR_BOOKKEEPER,
  business_owner: DEMO_TOUR_BUSINESS_OWNER,
  lender: DEMO_TOUR_LENDER,
  finance_team: DEMO_TOUR_FINANCE_TEAM,
  financial_investigator: DEMO_TOUR_INVESTIGATOR,
  other: DEMO_TOUR_OTHER
};
